/**
 * ListadoPage — Tabla de charters con filtros (estado, embarcación, fecha, búsqueda).
 * Endpoint: GET /turismo/charter/reservas
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, TextField, MenuItem, Chip, Button, InputAdornment,
  IconButton, Tooltip, CircularProgress,
} from '@mui/material';
import { useNavigate, useOutletContext } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { getFirebaseAuthHeaders } from '../common/firebaseAuth';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

async function getAuthHeaders() { return getFirebaseAuthHeaders(); }
const ESTADO_COLOR = {
  presupuesto: { bg: '#fef3c7', fg: '#92400e', label: 'Presupuesto' },
  confirmada:  { bg: '#dbeafe', fg: '#1e40af', label: 'Confirmada' },
  embarcada:   { bg: '#dcfce7', fg: '#166534', label: 'Embarcada' },
  completada:  { bg: '#e2e8f0', fg: '#334155', label: 'Completada' },
  cancelada:   { bg: '#fee2e2', fg: '#991b1b', label: 'Cancelada' },
};

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString('es-CO')}`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

// Devuelve YYYY-MM-DD para "hoy + offset días" en la zona local del navegador.
function fechaOffset(offsetDias = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDias);
  return d.toISOString().slice(0, 10);
}

// Chip visible sólo si fecha_alquiler = mañana, estado bloqueante y sin recordatorio
function debeMostrarRecordatorio(r) {
  return r.fecha_alquiler === fechaOffset(1)
    && ['confirmada', 'embarcada'].includes(r.estado)
    && !r.recordatorio_enviado_at;
}

function buildRecordatorioMsg(r) {
  const fecha = r.fecha_alquiler ? (() => {
    const [y, m, d] = r.fecha_alquiler.split('-'); return `${d}/${m}/${y}`;
  })() : '';
  return [
    `Hola ${r.cliente?.nombre || ''} 👋`,
    ``,
    `Recordatorio de tu *charter* para MAÑANA:`,
    ``,
    `📋 Código: *${r.codigo_reserva}*`,
    `🚤 Embarcación: ${r.embarcacion_nombre || '—'}`,
    `📅 Fecha: ${fecha}`,
    r.hora_zarpe ? `⏰ Zarpe: ${r.hora_zarpe}` : '',
    r.saldo_pendiente > 0
      ? `⚠️ Saldo pendiente: $${Number(r.saldo_pendiente).toLocaleString('es-CO')} (debe estar en cero para embarcar)`
      : `✅ Charter totalmente pagado`,
    ``,
    `¡Te esperamos! Cualquier duda por acá.`,
  ].filter(Boolean).join('\n');
}

export default function ListadoPage() {
  const navigate = useNavigate();
  const { empresaId, refreshCounts } = useOutletContext() || {};
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [embarcaciones, setEmbarcaciones] = useState([]);
  const [filtros, setFiltros] = useState({
    estado: '', embarcacion_id: '', fecha_desde: '', fecha_hasta: '', q: '',
  });

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ empresa_id: empresaId, limit: '200' });
      Object.entries(filtros).forEach(([k, v]) => { if (v) params.set(k, v); });
      const r = await fetch(`${API_BASE}/turismo/charter/reservas?${params}`,
        { headers: await getAuthHeaders() });
      if (r.ok) {
        const data = await r.json();
        setRows(data.items || []);
      }
    } catch (e) { console.error('[charter/listado]', e); }
    finally { setLoading(false); }
  }, [empresaId, filtros]);

  const cargarEmbarcaciones = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/turismo/embarcaciones?empresa_id=${empresaId}`,
        { headers: await getAuthHeaders() });
      if (r.ok) setEmbarcaciones(await r.json());
    } catch (e) { console.error(e); }
  }, [empresaId]);

  useEffect(() => { cargarEmbarcaciones(); }, [cargarEmbarcaciones]);
  useEffect(() => { cargar(); }, [cargar]);

  const setF = (k) => (e) => setFiltros(prev => ({ ...prev, [k]: e.target.value }));

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <Typography sx={{ fontSize: 22, fontWeight: 700, color: '#0f172a' }}>
            Charters
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#64748b' }}>
            Alquiler de embarcaciones completas · {rows.length} registros
          </Typography>
        </Box>
        <Tooltip title="Refrescar">
          <IconButton onClick={() => { cargar(); refreshCounts?.(); }}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
        <Button
          variant="contained"
          startIcon={<AddCircleIcon />}
          onClick={() => navigate('/charter/nuevo')}
          sx={{
            background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
            boxShadow: '0 4px 12px rgba(124, 58, 237, 0.35)',
            fontWeight: 700, textTransform: 'none',
            '&:hover': { background: 'linear-gradient(135deg, #6d28d9 0%, #9333ea 100%)' },
          }}
        >
          Nuevo charter
        </Button>
      </Box>

      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
        <TextField
          size="small" label="Buscar" value={filtros.q} onChange={setF('q')}
          placeholder="Código, cliente, WhatsApp…"
          sx={{ minWidth: 220, flex: 1 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        <TextField
          size="small" select label="Estado" value={filtros.estado} onChange={setF('estado')}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">Todos</MenuItem>
          <MenuItem value="presupuesto">Presupuesto</MenuItem>
          <MenuItem value="confirmada">Confirmada</MenuItem>
          <MenuItem value="embarcada">Embarcada</MenuItem>
          <MenuItem value="completada">Completada</MenuItem>
          <MenuItem value="cancelada">Cancelada</MenuItem>
        </TextField>
        <TextField
          size="small" select label="Embarcación" value={filtros.embarcacion_id} onChange={setF('embarcacion_id')}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">Todas</MenuItem>
          {embarcaciones.map(e => (
            <MenuItem key={e.id} value={e.id}>{e.nombre}</MenuItem>
          ))}
        </TextField>
        <TextField
          size="small" type="date" label="Desde" InputLabelProps={{ shrink: true }}
          value={filtros.fecha_desde} onChange={setF('fecha_desde')} sx={{ width: 150 }}
        />
        <TextField
          size="small" type="date" label="Hasta" InputLabelProps={{ shrink: true }}
          value={filtros.fecha_hasta} onChange={setF('fecha_hasta')} sx={{ width: 150 }}
        />
      </Paper>

      {/* Tabla */}
      <TableContainer component={Paper} sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <Table size="small" sx={{ minWidth: 900 }}>
          <TableHead sx={{ bgcolor: '#f8fafc' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, color: '#334155' }}>Código</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#334155' }}>Cliente</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#334155' }}>Embarcación</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#334155' }}>Fecha</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#334155' }}>Zarpe</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#334155' }} align="right">Pax</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#334155' }} align="right">Precio</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#334155' }} align="right">Saldo</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#334155' }}>Estado</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#334155' }} align="center">Ver</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} sx={{ color: '#7c3aed' }} />
                </TableCell>
              </TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4, color: '#94a3b8' }}>
                  Sin charters registrados. Crea el primero con "Nuevo charter".
                </TableCell>
              </TableRow>
            )}
            {!loading && rows.map(r => {
              const est = ESTADO_COLOR[r.estado] || { bg: '#e2e8f0', fg: '#334155', label: r.estado };
              return (
                <TableRow key={r.id} hover
                  onClick={() => navigate(`/charter/${r.id}`)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600, color: '#7c3aed' }}>
                    {r.codigo_reserva}
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{r.cliente?.nombre}</Typography>
                      <Typography sx={{ fontSize: 11, color: '#64748b' }}>{r.cliente?.whatsapp}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontSize: 13 }}>{r.embarcacion_nombre || '—'}</TableCell>
                  <TableCell sx={{ fontSize: 13 }}>{fmtDate(r.fecha_alquiler)}</TableCell>
                  <TableCell sx={{ fontSize: 13, color: '#64748b' }}>
                    {r.hora_zarpe || '—'}{r.hora_regreso ? ` → ${r.hora_regreso}` : ''}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 13 }}>{r.pax_total}</TableCell>
                  <TableCell align="right" sx={{ fontSize: 13, fontWeight: 600 }}>
                    {fmtMoney(r.precio_total)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 13, color: r.saldo_pendiente > 0 ? '#dc2626' : '#059669', fontWeight: 600 }}>
                    {fmtMoney(r.saldo_pendiente)}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                      <Chip label={est.label} size="small"
                        sx={{ bgcolor: est.bg, color: est.fg, fontWeight: 700, fontSize: 10.5, height: 22 }}
                      />
                      {debeMostrarRecordatorio(r) && (
                        <Chip
                          label="Recordar" size="small" icon={<WhatsAppIcon sx={{ fontSize: 12 }} />}
                          clickable
                          onClick={(e) => {
                            e.stopPropagation();
                            const num = String(r.cliente?.whatsapp || '').replace(/[^0-9]/g, '');
                            if (!num) return;
                            const msg = buildRecordatorioMsg(r);
                            window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, '_blank');
                            // Marcar como enviado (backend). onClick no puede ser async
                            // sin cambiar la firma; usamos IIFE async.
                            (async () => {
                              const headers = await getAuthHeaders();
                              await fetch(`${API_BASE}/turismo/charter/reservas/${r.id}/recordatorio-enviado?empresa_id=${empresaId}`,
                                { method: 'POST', headers });
                              cargar();
                            })();
                          }}
                          sx={{
                            bgcolor: '#7c3aed', color: '#fff', fontWeight: 700, fontSize: 10, height: 20,
                            '& .MuiChip-icon': { color: '#fff' },
                            '&:hover': { bgcolor: '#6d28d9' },
                          }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/charter/${r.id}`); }}>
                      <VisibilityIcon fontSize="small" sx={{ color: '#7c3aed' }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
